import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import cloneDeep from 'lodash.clonedeep';
import logger from '@widgets/logger';
import { useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import MicrositeFileUploader from '@features/MicrositeFileUploader';
import { ModalBase } from '@components/Modal';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { DeleteIcon } from 'assets/images/icons';
import { LOCALIZE_MODAL_LANGUAGES } from '@constants/microsites';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        outline: 'none',
        width: '100%',
        height: 420,
        maxWidth: 652,
        '@media (max-width: 600px)': {
            height: 'var(--100vh)',
            width: '100%',
            maxWidth: 'none',
        },
    },
    '& .modal-base-content': {
        padding: 16,
        overflow: 'auto',
        maxHeight: 'calc(100% - 118px)',
    },
});

const StyledIconButton = styled(IconButton)({
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 4,
    height: 40,
    width: 40,
    padding: 2,
    gridArea: 'button',
    '&:not(:last-child)': {
        marginRight: 8,
    },
    '& svg': {
        width: '18px',
        height: '18px',
        '& path': {
            fill: 'rgba(0, 0, 0, 0.6)',
        },
    },
});

const Overline = styled(Typography)({
    fontWeight: '500 !important',
    fontSize: '10px !important',
    lineHeight: '12px !important',
    letterSpacing: '1.5px !important',
    textTransform: 'uppercase',
    color: 'rgba(0, 0, 0, 0.6)',
    display: 'block',
});

const LanguageWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'withEntity' && prop !== 'withTitle',
})(({ withEntity, withTitle }) => ({
    display: 'grid',
    gridGap: 16,
    gridTemplateColumns: `160px ${withTitle && withEntity ? '160px 1fr' : withTitle || withEntity ? '1fr' : ''}`,
    marginBottom: 8,
    '@media (max-width: 600px)': {
        gridTemplateColumns: '100%',
        gridGap: 0,
    },
}));

const AdditionalLanguageWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'withEntity' && prop !== 'withTitle',
})(({ withEntity, withTitle }) => ({
    display: 'grid',
    gridGap: 16,
    gridTemplateAreas: withEntity
        ? withTitle
            ? '"select input1 input2 button"'
            : '"select input2 button"'
        : withTitle
        ? '"select input1 button"'
        : '"select button"',
    gridTemplateColumns:
        withEntity && withTitle ? '160px 160px 1fr 40px' : withEntity || withTitle ? '160px 1fr 40px' : '160px 40px',
    marginBottom: 8,
    '@media (max-width: 600px)': {
        gridTemplateAreas: `"select button" ${withTitle ? '"input1 input1" "input2 input2"' : '"input2 input2"'}`,
        gridTemplateColumns: '1fr 40px',
    },
}));

const LocalizeItemModal = ({
    modal,
    propName,
    entity,
    withChangeTitle = true,
    withChangeEntity,
    withUploadFile,
    onUploadFile,
    onClose,
    currentLocale,
}) => {
    const [items, setItems] = useState({});
    const [isUploadingFile, setIsUploadingFile] = useState(false);

    const account = useSelector(state => state.account?.account);

    const { setValue, watch } = useFormContext();
    const { t } = useTranslation();

    const genericItems = watch(entity);

    useEffect(() => {
        setItems(genericItems);
    }, []);

    const languageOptions = LOCALIZE_MODAL_LANGUAGES[account.locale];

    const availableLanguages = useMemo(() => {
        return languageOptions?.filter(lang => !items[modal.index]?.some(l => l.language === lang.value));
    }, [items, modal.index]);

    const onChangeLink = (e, language) => {
        const itemsCopy = cloneDeep(items);
        itemsCopy[modal.index] = itemsCopy[modal.index].map(l =>
            l.language === language ? { ...l, [e.target.name]: e.target.value } : l,
        );
        setItems(itemsCopy);
    };

    const onChangeFile = async (e, language) => {
        const itemsCopy = cloneDeep(items);
        const file = e.target.files[0];
        const linkIndex = itemsCopy[modal.index].findIndex(l => l.language === language);
        if (!file) return;
        try {
            setIsUploadingFile(true);
            const data = new FormData();
            data.append('file', e.target.files[0]);
            data.append('fileName', e.target.files[0].name);
            const response = await onUploadFile(data);
            itemsCopy[modal.index][linkIndex] = { ...itemsCopy[modal.index][linkIndex], fileUrl: response.publicUrl };
            setItems(itemsCopy);
        } catch (_) {
            logger.error("Couldn't upload file microsite");
        } finally {
            setIsUploadingFile(false);
            e.target.value = '';
        }
    };

    const onAddLanguage = () => {
        const itemsCopy = cloneDeep(items);
        if (modal?.ctx?.isSurvey) {
            itemsCopy[modal.index].push({
                surveyId: items[modal.index]?.[0]?.surveyId,
                title: t('common.title'),
                link: '',
                language: availableLanguages[0].value || '',
            });
        } else if (modal?.ctx?.isFile) {
            itemsCopy[modal.index].push({
                fileUrl: '',
                title: items[modal.index]?.[0]?.title,
                link: '',
                language: availableLanguages[0].value || '',
            });
        } else {
            itemsCopy[modal.index].push({
                title: items[modal.index]?.[0]?.title,
                [propName]: '',
                language: availableLanguages[0].value || '',
            });
        }
        setItems(itemsCopy);
    };

    const onDeleteAdditional = language => {
        const itemsCopy = cloneDeep(items);
        itemsCopy[modal.index] = itemsCopy[modal.index].filter(l => l.language !== language);
        setItems(itemsCopy);
    };

    const onSave = () => {
        setValue(entity, items, { shouldDirty: true });
        onClose();
    };

    const isValid = useMemo(
        () =>
            items[modal.index]?.every(item => !!item.title && (!!item[propName] || !!item.surveyId || !!item.fileUrl)),
        [items, modal.index],
    );

    if (!items[modal.index]) return null;

    const firstItem = items[modal.index].find(l => l.language === currentLocale) || items[modal.index][0];

    const localizedItems = items[modal.index].filter(l => l.language !== firstItem.language);

    return (
        <Modal
            isOpen={modal.open}
            handleClose={onClose}
            title={`${t('common.localization')} - ${firstItem?.title || ''}`}
            onPrimaryAction={onSave}
            primaryActionDisabled={!isValid}
            primaryActionText={t('buttons.save')}
            onSecondaryAction={onClose}
            secondaryActionText={t('buttons.cancel')}
        >
            <div>
                <Overline variant="caption" sx={{ marginBottom: '16px' }}>
                    {t('LocalizeItemModal.defaultLanguages')}
                </Overline>
                <LanguageWrapper withTitle={withChangeTitle} withEntity={withChangeEntity}>
                    <Select
                        value={firstItem?.language}
                        name="language"
                        onChange={e => onChangeLink(e, firstItem?.language)}
                        label={t('common.language')}
                        disabled
                        fullWidth
                        options={languageOptions}
                    />
                    {withChangeTitle ? (
                        <Input
                            inputProps={{
                                name: 'title',
                                value: firstItem?.title,
                                onChange: e => onChangeLink(e, firstItem?.language),
                            }}
                            error={!firstItem?.title}
                            helperText={!firstItem?.title ? t('validation.required') : ''}
                            required
                            label={t('common.title')}
                            fullWidth
                        />
                    ) : null}
                    {withChangeEntity && !withUploadFile ? (
                        <Input
                            inputProps={{
                                name: propName,
                                value: firstItem?.[propName],
                                onChange: e => onChangeLink(e, firstItem?.language),
                            }}
                            label={t(`common.${propName}`)}
                            required
                            error={!firstItem?.[propName]}
                            helperText={!firstItem?.[propName] ? t('validation.required') : ''}
                            fullWidth
                        />
                    ) : null}
                    {withUploadFile ? (
                        <MicrositeFileUploader
                            item={firstItem}
                            disabled={isUploadingFile}
                            onChange={e => onChangeFile(e, firstItem?.language)}
                        />
                    ) : null}
                </LanguageWrapper>
                <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}
                >
                    <Overline variant="caption">{t('LocalizeItemModal.additionalLanguages')}</Overline>
                    <Button variant="outlined" onClick={onAddLanguage} disabled={!availableLanguages.length}>
                        {t('buttons.addLanguage')}
                    </Button>
                </div>
                {localizedItems.map((item, index) => {
                    const selectedLanguage = languageOptions.find(l => l.value === item.language);
                    const langOptions = [...(selectedLanguage ? [selectedLanguage] : []), ...availableLanguages];
                    return (
                        <AdditionalLanguageWrapper
                            withTitle={withChangeTitle}
                            withEntity={withChangeEntity}
                            key={index}
                        >
                            <Select
                                value={item?.language}
                                name="language"
                                onChange={e => onChangeLink(e, item?.language)}
                                label={t('common.language')}
                                fullWidth
                                options={langOptions}
                                style={{ gridArea: 'select' }}
                            />
                            {withChangeTitle ? (
                                <Input
                                    inputProps={{
                                        name: 'title',
                                        value: item?.title,
                                        onChange: e => onChangeLink(e, item?.language),
                                    }}
                                    required
                                    error={!item?.title}
                                    helperText={!item?.title ? t('validation.required') : ''}
                                    sx={{ gridArea: 'input1' }}
                                    label={t('common.title')}
                                    fullWidth
                                />
                            ) : null}
                            {withChangeEntity && !withUploadFile ? (
                                <Input
                                    inputProps={{
                                        name: propName,
                                        value: item?.[propName],
                                        onChange: e => onChangeLink(e, item?.language),
                                    }}
                                    required
                                    error={!item?.[propName]}
                                    helperText={!item?.[propName] ? t('validation.required') : ''}
                                    sx={{ gridArea: 'input2' }}
                                    label={t(`common.${propName}`)}
                                    fullWidth
                                />
                            ) : null}
                            {withUploadFile ? (
                                <MicrositeFileUploader
                                    item={item}
                                    disabled={isUploadingFile}
                                    onChange={e => onChangeFile(e, item?.language)}
                                    styles={{ gridArea: 'input2' }}
                                />
                            ) : null}
                            <StyledIconButton onClick={() => onDeleteAdditional(item?.language)}>
                                <DeleteIcon />
                            </StyledIconButton>
                        </AdditionalLanguageWrapper>
                    );
                })}
            </div>
        </Modal>
    );
};

export default LocalizeItemModal;
